/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Data masking", description = "Detect sensitive columns automatically and control how each one can be viewed, used in audiences, and synced with privacy levels.", lastUpdatedDate = new Date(1790553600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Data masking",
        description: "Detect sensitive columns automatically and control how each one can be viewed, used in audiences, and synced with privacy levels.",
        lastUpdatedDate: "Mon Sep 28 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Privacy levels",
          "depth": 0,
          "id": "privacy-levels"
        }, {
          "value": "Review flagged columns",
          "depth": 0,
          "id": "review-flagged-columns"
        }, {
          "value": "Set a privacy level",
          "depth": 0,
          "id": "set-a-privacy-level"
        }, {
          "value": "Categorize columns",
          "depth": 0,
          "id": "categorize-columns"
        }, {
          "value": "Permissions",
          "depth": 0,
          "id": "permissions"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      img: "img",
      code: "code",
      ul: "ul",
      li: "li"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Platform admins"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/customer-studio/overview#for-data-teams-and-platform-admins",
                children: "Set up for platform admins and data teams"
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Data masking protects sensitive columns by controlling how each one can be viewed, used in audiences, and synced. Hightouch flags likely personally identifiable information (PII) for review, and you assign a privacy level that governs the column everywhere it's used."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Data masking lets you classify sensitive columns and control how each one can be used across Hightouch. You assign every column a privacy level, and that level applies wherever the column appears: profiles, filter suggestions, analytics, agent responses, and syncs."
      }), "\n", _jsxs(_components.p, {
        children: ["You manage privacy levels on the ", _jsx(_components.strong, {
          children: "Data masking"
        }), " page, under ", _jsx(_components.strong, {
          children: "Customer Studio > Governance"
        }), ". You can set the same level per column ", _jsx(_components.a, {
          href: "/customer-studio/schema#set-privacy-levels-for-columns",
          children: "as you build your schema"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["Data masking controls ", _jsx(_components.strong, {
          children: "columns and values"
        }), ". To restrict which ", _jsx(_components.strong, {
          children: "rows"
        }), " a user can access instead, use ", _jsx(_components.a, {
          href: "/customer-studio/subsets",
          children: "subsets"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "privacy-levels",
        children: "Privacy levels"
      }), "\n", _jsx(_components.p, {
        children: "Every column has a privacy level. Assign one based on how sensitive the data is and where it's allowed to be used."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Privacy level"
            }), _jsx(_components.th, {
              children: "What it does"
            }), _jsx(_components.th, {
              children: "Use for"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Unreviewed"
              })
            }), _jsx(_components.td, {
              children: "Behaves normally until you classify it. This is the default."
            }), _jsx(_components.td, {
              children: "Columns you haven't reviewed yet."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Approved"
              })
            }), _jsx(_components.td, {
              children: "No restrictions. Cleared through your review process."
            }), _jsx(_components.td, {
              children: "Non-sensitive data."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Redacted"
              })
            }), _jsx(_components.td, {
              children: "Values are hidden from users in the app, but the column can still be used in syncs and audience logic."
            }), _jsx(_components.td, {
              children: "Data you can sync but shouldn't show users, such as email addresses."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Sync-only"
              })
            }), _jsx(_components.td, {
              children: "Syncs to destinations, but isn't visible or usable in the app."
            }), _jsx(_components.td, {
              children: "Values needed downstream but never in Hightouch."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Sync-blocked"
              })
            }), _jsx(_components.td, {
              children: "Usable inside Hightouch, but never synced to any destination."
            }), _jsx(_components.td, {
              children: "Internal attributes that must not leave the warehouse."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Blocked"
              })
            }), _jsx(_components.td, {
              children: "Never synced, displayed, or used downstream."
            }), _jsx(_components.td, {
              children: "Highly sensitive data that must stay fully restricted."
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/data-masking/privacy-level-list.png",
          alt: "Privacy levels"
        })
      }), "\n", _jsx(_components.h2, {
        id: "review-flagged-columns",
        children: "Review flagged columns"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch automatically flags columns that look like PII and marks them with a warning icon for review. Flagging only applies to unreviewed columns, so it never overrides a level you've already set."
      }), "\n", _jsxs(_components.p, {
        children: ["Filter by ", _jsx(_components.strong, {
          children: "PII review"
        }), " for the columns that need review, check each one, and assign the correct privacy level."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/data-masking/pii-flag.png",
          alt: "PII flagged column"
        })
      }), "\n", _jsx(_components.h2, {
        id: "set-a-privacy-level",
        children: "Set a privacy level"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to ", _jsx(_components.strong, {
          children: "Customer Studio > Governance > Data masking"
        }), ". The table lists every column, sorted by most recently updated. Search or filter by model, source, PII category, privacy level, or PII review status to find the columns you want."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/data-masking/privacy-page.png",
          alt: "Data masking table listing columns with their model, source, PII category, and privacy level, with the filter rail open"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Find the column you want to classify, then set its level from the ", _jsx(_components.strong, {
          children: "Privacy level"
        }), " dropdown on that row. The change takes effect as soon as you select a level, and clears any PII flag on the column."]
      }), "\n", _jsxs(_components.p, {
        children: ["To keep an audit record or review classifications outside Hightouch, select ", _jsx(_components.strong, {
          children: "Export CSV"
        }), ". The download lists each column with its model, source, PII category, and privacy level, and respects the filters you've applied."]
      }), "\n", _jsx(_components.h2, {
        id: "categorize-columns",
        children: "Categorize columns"
      }), "\n", _jsxs(_components.p, {
        children: ["Assign a category to group and filter related columns, such as ", _jsx(_components.code, {
          children: "Email"
        }), ", ", _jsx(_components.code, {
          children: "Name"
        }), ", ", _jsx(_components.code, {
          children: "Birthdate"
        }), ", ", _jsx(_components.code, {
          children: "Address"
        }), ", ", _jsx(_components.code, {
          children: "Phone"
        }), ", or ", _jsx(_components.code, {
          children: "IP address"
        }), ". Categories make it easier to review and manage PII across a large schema."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/data-masking/pii-category.png",
          alt: "PII categories"
        })
      }), "\n", _jsx(_components.h2, {
        id: "permissions",
        children: "Permissions"
      }), "\n", _jsxs(_components.p, {
        children: ["Data masking is controlled by schema permissions. To edit privacy levels and categories, a user must have ", _jsx(_components.code, {
          children: "Configure schema"
        }), " access on the source."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/data-masking/data-masking-configure-schema-permissions.png",
          alt: "Data masking configure schema permissions"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["See ", _jsx(_components.a, {
          href: "/workspace-management/roles#sources",
          children: "source-level permissions"
        }), " for details."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/workspace-management/lineage",
            children: "Usage"
          }), " to see which audiences, syncs, and journeys depend on a column before you change its privacy level."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/subsets",
            children: "Subsets"
          }), " to restrict which rows of data each user can access."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/schema#set-privacy-levels-for-columns",
            children: "Define your schema"
          }), " to set privacy levels as you build the schema."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/destination-rules",
            children: "Destination rules"
          }), " to control which records can reach a given destination."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
